import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge.tsx';
import { getLead } from '@/services/api';
import {
    User,
    Clock,
    Workflow,
    Globe,
    MousePointer,
    X,
    MessageCircle,
    CheckCircle,
    AlertTriangle
} from 'lucide-react';
import { formatDate, formatCountryWithFlag, getStatusLabel } from '../utils/leadUtils.ts';

interface ViewLeadModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    leadId: number | null;
}

export function ViewLeadModal({ open, onOpenChange, leadId }: ViewLeadModalProps) {
    const { data: lead, isLoading } = useQuery({
        queryKey: ['lead', leadId],
        queryFn: () => getLead(leadId!),
        enabled: open && !!leadId,
    });

    if (!open) return null;

    const fullName = lead ? `${lead.nome} ${lead.sobrenome || ''}`.trim() : '';

    return (
        <div className="fixed inset-0 z-50">
            {/* Overlay - click to close */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => onOpenChange(false)}
            />

            {/* Centered floating modal */}
            <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[750px] max-h-[85vh] overflow-hidden pointer-events-auto"
                >
                    {/* Close button */}
                    <button
                        onClick={() => onOpenChange(false)}
                        className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {isLoading && (
                        <div className="flex items-center justify-center py-24">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]" />
                            <p className="ml-3 text-gray-500">Carregando...</p>
                        </div>
                    )}

                    {!isLoading && lead && (
                        <div className="flex flex-col md:flex-row max-h-[85vh]">
                            {/* Left Panel - Profile */}
                            <div className="w-full md:w-[240px] bg-gray-50/50 border-r border-gray-100 p-6 flex flex-col items-center shrink-0">
                                {/* Avatar */}
                                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] flex items-center justify-center text-white text-3xl font-bold mb-4 shadow-xl ring-4 ring-[#7dd3fc]/20">
                                    {lead.nome.charAt(0).toUpperCase()}
                                </div>

                                {lead.is_active ? (
                                    <div className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium mb-5">
                                        <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                                        <span>Inscrito</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1.5 text-sm text-red-600 font-medium mb-5">
                                        <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                                        <span>Não Visado</span>
                                    </div>
                                )}

                                <div className="w-full space-y-3 text-sm">
                                    {lead.sexo && (
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <User className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={1.5} />
                                            <span>{lead.sexo}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Globe className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={1.5} />
                                        <span>{formatCountryWithFlag(lead.pais)}</span>
                                    </div>
                                    {(lead.cidade || lead.estado) && (
                                        <div className="flex items-center gap-2 text-gray-600 pl-6">
                                            <span>{lead.cidade}{lead.estado ? `, ${lead.estado}` : ''}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Clock className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={1.5} />
                                        <span className="text-xs">
                                            {lead.last_interaction_at ? formatDate(lead.last_interaction_at) : 'Desconhecido'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <MessageCircle className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={1.5} />
                                        <span className="font-mono text-xs break-all">{lead.sender_id}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel - Details */}
                            <div className="flex-1 p-6 space-y-5 overflow-y-auto">
                                <h2 className="text-xl font-bold text-gray-900 pr-8">{fullName}</h2>

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Tags do contato</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {lead.tags.length > 0 ? (
                                            lead.tags.map((tag) => (
                                                <span
                                                    key={tag.id}
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border"
                                                    style={{
                                                        borderColor: tag.cor || '#e5e7eb',
                                                        color: tag.cor || '#374151',
                                                        backgroundColor: tag.cor ? `${tag.cor}15` : '#f9fafb'
                                                    }}
                                                >
                                                    {tag.nome}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-400">Nenhuma tag</span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Status</h3>
                                    <Badge className={`rounded-lg text-sm px-3 py-1 border ${!lead.is_active
                                            ? 'bg-red-50 text-red-700 border-red-200'
                                            : lead.in_24h_window
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : 'bg-orange-50 text-orange-700 border-orange-200'
                                        }`}>
                                        {getStatusLabel(lead.is_active, lead.in_24h_window)}
                                    </Badge>
                                </div>

                                {lead.current_funnel_id && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-2">Inscrito no Funil</h3>
                                        <div className="bg-cyan-50 border border-cyan-200/50 rounded-xl p-3">
                                            <div className="flex items-center gap-2">
                                                <Workflow className="w-5 h-5 text-cyan-600" strokeWidth={1.5} />
                                                <span className="text-sm font-medium text-cyan-700">
                                                    Funil #{lead.current_funnel_id}
                                                </span>
                                            </div>
                                            {lead.last_node_id && (
                                                <p className="text-xs text-cyan-600 mt-1 ml-7">
                                                    Último nó: #{lead.last_node_id}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Campos do Sistema</h3>
                                    <div className="space-y-2">
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2.5">
                                            <p className="text-xs text-gray-400">Primeiro Nome:</p>
                                            <p className="text-sm font-medium text-gray-900">{lead.nome}</p>
                                        </div>
                                        {lead.sobrenome && (
                                            <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2.5">
                                                <p className="text-xs text-gray-400">Sobrenome:</p>
                                                <p className="text-sm font-medium text-gray-900">{lead.sobrenome}</p>
                                            </div>
                                        )}
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2.5">
                                            <p className="text-xs text-gray-400">Sexo:</p>
                                            <p className="text-sm font-medium text-gray-900">{lead.sexo || 'Não informado'}</p>
                                        </div>
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2.5">
                                            <p className="text-xs text-gray-400">Total de Clicks:</p>
                                            <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                                <MousePointer className="w-4 h-4 text-indigo-500" strokeWidth={1.5} />
                                                {lead.total_clicks}
                                            </p>
                                        </div>
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2.5">
                                            <p className="text-xs text-gray-400">Cadastrado em:</p>
                                            <p className="text-sm font-medium text-gray-900">{formatDate(lead.created_at)}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Informações Técnicas</h3>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex items-center justify-between py-2 border-b border-gray-50">
                                            <span className="text-gray-500">Lead ID</span>
                                            <code className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-mono">{lead.id}</code>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-gray-50">
                                            <span className="text-gray-500">Sender ID</span>
                                            <code className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-mono">{lead.sender_id}</code>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-gray-50">
                                            <span className="text-gray-500">Página</span>
                                            <span className="text-sm font-medium text-gray-900">{lead.page_name || lead.page_id}</span>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-gray-50">
                                            <span className="text-gray-500">Page ID</span>
                                            <code className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-mono">{lead.page_id}</code>
                                        </div>
                                        {lead.last_error_code && (
                                            <div className="flex items-center justify-between py-2">
                                                <span className="text-gray-500">Último Erro</span>
                                                <code className="bg-red-50 text-red-700 px-2.5 py-1 rounded-lg text-xs font-mono">{lead.last_error_code}</code>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}